import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Typography,
  Stack,
  Link,
  styled,
  CircularProgress,
} from '@mui/material';
import { OnboardingLayout } from '~/shared/components/OnboardingLayout';
import { PrimaryButton } from '~/modules/onboarding/components/PrimaryButton';
import InvitationRadioGroup from '~/modules/invitation/components/InvitationRadioGroup';
import InvitationConsentCheckbox from '~/modules/invitation/components/InvitationConsentCheckbox';
import { GET_INVITATION_DETAILS } from '~/graphql/queries';
import { CREATE_PROGRAMMATIC_USER } from '~/graphql/mutations';
import {
  LocalStorageKeys,
  ROUTES,
  SENTRY_OPERATIONS,
} from '~/shared/constants';
import { EXTERNAL_URLS } from 'common';
import { theme } from '~/shared/styles/theme';
import { reportError } from '~/shared/utils/sentry';
import introGraphic from '~/assets/intro-graphic.png';
import { Lock } from '~/assets/icons';

interface InvitationFormData {
  decision: 'yes' | 'no' | '';
  consent: boolean;
}

const InvitationAcceptance = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { loginWithRedirect } = useAuth0();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data, loading, error } = useQuery(GET_INVITATION_DETAILS, {
    variables: { token: token ?? '' },
    skip: !token,
  });

  const [createProgrammaticUser] = useMutation(CREATE_PROGRAMMATIC_USER);

  const { control, watch, handleSubmit } = useForm<InvitationFormData>({
    defaultValues: {
      decision: '',
      consent: false,
    },
  });

  const decision = watch('decision');
  const consent = watch('consent');
  const isFormValid = decision !== '' && consent;

  const onSubmit = async (formData: InvitationFormData) => {
    if (!data?.getInvitationDetails || !token) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    const invitationDetails = data.getInvitationDetails;

    if (formData.decision === 'yes') {
      // "Yes" path: Redirect to Auth0 signup with invitation context

      try {
        await loginWithRedirect({
          authorizationParams: {
            login_hint: invitationDetails.email,
            screen_hint: 'signup',
          },
          appState: {
            invitationToken: token,
            invitationDecision: 'accept',
          },
        });
      } catch (error) {
        reportError(error, {
          operation: SENTRY_OPERATIONS.INVITATION_LOGIN_REDIRECT,
          extra: {
            invitationToken: token,
            decision: 'accept',
          },
        });

        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to redirect to login. Please try again.';
        setSubmitError(errorMessage);
        setIsSubmitting(false);
      }
    } else {
      // "No" path: Create account programmatically without interactive signup
      try {
        const result = await createProgrammaticUser({
          variables: {
            input: {
              email: invitationDetails.email,
              invitationToken: token,
            },
          },
        });

        const success = result.data?.createProgrammaticUser?.success;

        if (!success) {
          throw new Error('Failed to create account');
        }

        // Store invitation data for processing after provisionUser
        localStorage.setItem(LocalStorageKeys.InvitationToken, token);
        localStorage.setItem(LocalStorageKeys.InvitationDecision, 'decline');

        navigate(ROUTES.CONSENT);
      } catch (error) {
        reportError(error, {
          operation: SENTRY_OPERATIONS.INVITATION_CREATE_PROGRAMMATIC_USER,
          extra: {
            invitationToken: token,
            decision: 'decline',
          },
        });

        const errorMessage =
          error instanceof Error ? error.message : 'Failed to create account';
        setSubmitError(errorMessage);
        setIsSubmitting(false);
      }
    }
  };

  if (loading) {
    return (
      <OnboardingLayout>
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      </OnboardingLayout>
    );
  }

  if (error || !data?.getInvitationDetails) {
    return (
      <OnboardingLayout>
        <ContentContainer>
          <ErrorText>
            Invalid or expired invitation. Please contact the person who invited
            you.
          </ErrorText>
        </ContentContainer>
      </OnboardingLayout>
    );
  }

  const invitationDetails = data.getInvitationDetails;
  const inviterName =
    `${invitationDetails.inviterFirstName || ''} ${invitationDetails.inviterLastName || ''}`.trim();
  const familyLastName = invitationDetails.inviterLastName || '';

  const radioOptions = [
    {
      value: 'yes',
      label: 'Yes, Join Family Tree',
    },
    {
      value: 'no',
      label: "No, I'd rather not",
    },
  ];

  return (
    <OnboardingLayout>
      <ContentContainer>
        <HeaderIcon src={introGraphic} alt="DNA and computer icon" />

        <Heading>
          You've been invited to join your family's private tree
        </Heading>

        <Subheading>
          {inviterName} is using Quoiden to build a secure family tree to better
          understand OTC deficiency, a rare inherited condition that can affect
          future generations.
        </Subheading>

        <QuestionText>
          Would you like to join the {familyLastName} family tree?
        </QuestionText>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack>
            <InvitationRadioGroup
              name="decision"
              control={control}
              options={radioOptions}
            />

            <InvitationConsentCheckbox
              name="consent"
              control={control}
              label={
                <Typography fontSize="14px">
                  By selecting this checkbox, I agree to Quoiden's{' '}
                  <Link
                    href={EXTERNAL_URLS.TERMS_OF_SERVICE}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Service
                  </Link>
                  ,{' '}
                  <Link
                    href={EXTERNAL_URLS.PRIVACY_STATEMENT}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Statement
                  </Link>
                  , and{' '}
                  <Link
                    href={EXTERNAL_URLS.COOKIE_POLICY}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Cookie Policy
                  </Link>
                  .
                </Typography>
              }
            />

            {submitError && <ErrorText>{submitError}</ErrorText>}

            <PrimaryButton
              type="submit"
              disabled={!isFormValid || isSubmitting}
              fullWidth
              variant="brown"
              sx={{
                marginBottom: '20px',
              }}
            >
              {isSubmitting ? 'Creating Account...' : 'Continue'}
            </PrimaryButton>

            <SecurityMessage>
              <Lock />
              <SecurityText>
                Data is secure, consent-driven, and in your control
              </SecurityText>
            </SecurityMessage>
          </Stack>
        </form>
      </ContentContainer>
    </OnboardingLayout>
  );
};

export default InvitationAcceptance;

const ContentContainer = styled(Stack)({
  alignItems: 'center',
  width: '100%',
  maxWidth: '600px',
  margin: '0 auto',
});

const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '400px',
});

const ErrorText = styled(Typography)({
  fontSize: '18px',
  fontWeight: 500,
  color: theme.palette.error.main,
  textAlign: 'center',
  padding: theme.spacing(4),
});

const HeaderIcon = styled('img')({
  width: '178px',
  height: '137px',
  marginBottom: theme.spacing(4),
});

const Heading = styled(Typography)({
  fontSize: '25px',
  fontWeight: 500,
  color: theme.palette.custom.darkBrown,
  textAlign: 'center',
  marginBottom: '15px',
  lineHeight: 1.2,
});

const Subheading = styled(Typography)({
  fontSize: '16px',
  fontWeight: 400,
  color: theme.palette.custom.mediumBrown,
  textAlign: 'center',
  lineHeight: 1.5,
  marginBottom: '0px',
});

const QuestionText = styled(Subheading)({
  color: theme.palette.custom.textSupporting,
  textAlign: 'center',
  marginBottom: '0px',
  marginTop: '24px',
});

const SecurityMessage = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
});

const SecurityText = styled(Typography)({
  fontSize: '12px',
  fontWeight: 400,
  color: theme.palette.custom.mediumBrown,
});
